import { defineStore } from 'pinia';
import { ref } from 'vue';

import { ICompetitionsCreateResponse, ICompetitor, ICompetitorWithLabel } from '@pssa/types';

import { xhr } from '@/helpers/service.ts';

import { useResultsStore } from './useResultsStore';

export const useCompetitorStore = defineStore('competitior', () => {
  const resultStore = useResultsStore();
  const competitors = ref<ICompetitorWithLabel[]>([]);

  async function getCompetitors() {
    if (competitors.value.length === 0) {
      const response: ICompetitor[] = await xhr('/competitors');

      if (response) {
        competitors.value = response.map((c) => ({
          ...c,
          value: c._id,
          label: c.name,
        }));
      }
    }
  }

  async function createCompetitor(body: ICompetitor) {
    const response: ICompetitionsCreateResponse = await xhr('/competitors', {
      method: 'post',
      body,
    });

    const competitor = {
      ...response,
      value: response._id,
      label: response.name,
    };

    competitors.value.push(competitor);
    return competitor;
  }

  function findCompetitor(id: string) {
    return competitors.value.find((competitor) => competitor._id === id);
  }

  function findCompetitorIndex(id: string) {
    return competitors.value.findIndex((competitor) => competitor._id === id);
  }

  function findResultIndex(competitorId: string | undefined) {
    return resultStore.results.findIndex(({ personId }) => personId === competitorId);
  }

  async function updateCompetitor(competitor: ICompetitor) {
    const response = await xhr(`/competitors/${competitor._id}`, {
      method: 'put',
      body: competitor,
    });

    const competitorIndex = findCompetitorIndex(competitor._id);
    const resultIndex = findResultIndex(competitor._id);

    if (competitorIndex >= 0) {
      competitors.value[competitorIndex] = {
        ...competitor,
        value: competitor._id,
        label: competitor.name,
      };
    }

    if (resultIndex >= 0) {
      resultStore.results[resultIndex] = {
        ...resultStore.results[resultIndex],
        competitor: competitor.name,
        club: 'PSSA', // TODO: pass actual club
      };
    }

    return response;
  }

  async function mergeCompetitor(oldId: string, newId: string) {
    const response = await xhr(`/competitors/merge/${oldId}/${newId}`, {
      method: 'put',
    });

    const oldCompetitor = findCompetitor(oldId);
    const newCompetitor = findCompetitor(newId);
    const oldCompetitorInResultIndex = findResultIndex(oldCompetitor?._id);

    competitors.value = competitors.value.filter(({ _id }) => oldId !== _id);

    if (oldCompetitorInResultIndex !== -1 && newCompetitor) {
      resultStore.results[oldCompetitorInResultIndex] = {
        ...resultStore.results[oldCompetitorInResultIndex],
        competitor: newCompetitor.name,
        club: newCompetitor.club,
      };
    }

    return response;
  }

  return {
    competitors,
    createCompetitor,
    findCompetitor,
    getCompetitors,
    mergeCompetitor,
    updateCompetitor,
  };
});
